import { useEffect, useState } from "react";
import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import { Common } from "@/components/common";
import { Icon } from "@/components/Icons";
import {
  RedirectToOUI,
  Table,
  Td,
  Th,
  CustomScrollbar,
  ModalContent,
} from "@/components";
import { useAppDispatch, useAppSelector } from "@/sharedStore/hooks";
import {
  fetchAssignments,
  fetchUnassignedLists,
  setContext,
} from "./providers/slice";
import {
  useAssignList,
  useDeleteAssignment,
  useToggleAssignment,
} from "./services/customerExclusiveList";
import { SnackbarUtils } from "@/utils";
import axios from "axios";

interface ProductListItem {
  productSKU: string;
  productName: string;
  productUnit: string;
}

const tableHeaderClasses = "bg-neutral-20 font-bold";
const tableCellClasses =
  "!whitespace-normal border-b border-neutral-20 px-2 py-1";

const CustomerExclusiveListPage = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const customerAccountId = searchParams.get("CustomerAccountId") || "";
  const supplierAccountId = searchParams.get("SupplierAccountId") || "";

  const assignments = useAppSelector(
    (s) => s.customerExclusiveListReducer.assignments
  );
  const unassignedLists = useAppSelector(
    (s) => s.customerExclusiveListReducer.unassignedLists
  );
  const loading = useAppSelector((s) => s.customerExclusiveListReducer.loading);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignComboboxKey, setAssignComboboxKey] = useState(0);
  const [pendingList, setPendingList] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [modalProducts, setModalProducts] = useState<ProductListItem[]>([]);
  const [modalListName, setModalListName] = useState("");
  const [loadingModal, setLoadingModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const assignMuta = useAssignList();
  const toggleMuta = useToggleAssignment();
  const deleteMuta = useDeleteAssignment();

  const loadData = () => {
    if (customerAccountId && supplierAccountId) {
      dispatch(fetchAssignments({ customerAccountId, supplierAccountId }));
      dispatch(fetchUnassignedLists(customerAccountId));
    }
  };

  useEffect(() => {
    dispatch(setContext({ customerAccountId, supplierAccountId }));
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAccountId, supplierAccountId]);

  const openAssignModal = () => {
    setAssignSearch("");
    setPendingList(null);
    setAssignComboboxKey((k) => k + 1);
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setAssignSearch("");
    setPendingList(null);
  };

  const handleAssign = async () => {
    if (!pendingList) return;
    const res = await assignMuta.mutateAsync({
      customerAccountId,
      exclusiveListId: pendingList.id,
    });
    if (res?.data?.success) {
      SnackbarUtils.success(`"${pendingList.name}" assigned.`);
      closeAssignModal();
      loadData();
    }
  };

  const handleToggle = async (id: string, current: boolean) => {
    await toggleMuta.mutateAsync({ id, isActive: !current });
    loadData();
  };

  const handleDelete = (id: string, listName: string) => {
    setConfirmDelete({ id, name: listName });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await deleteMuta.mutateAsync(confirmDelete.id);
    SnackbarUtils.success("Assignment removed.");
    setConfirmDelete(null);
    loadData();
  };

  const handleShowProducts = async (
    exclusiveListId: string,
    listName: string
  ) => {
    setModalListName(listName);
    setLoadingModal(true);
    setShowProductsModal(true);
    try {
      const res = await axios.get("exclusivelists/detail", {
        params: { id: exclusiveListId },
      });
      setModalProducts(res.data?.data?.products || []);
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <div className="flex flex-col p-4 bg-white rounded shadow h-full">
        {/* Header */}
        <div className="flex items-center pb-4 mb-4 shadow-underline">
          <RedirectToOUI
            to={`/relationship/accounts/editcustomer/${customerAccountId}`}
          >
            <Icon.Back iconType="fill" className="fill-neutral-90" />
          </RedirectToOUI>
          <p className="mx-2 text-hd5 font-medium">Exclusive product lists</p>
        </div>

        <div className="flex justify-end mb-4">
          <Common.Button
            size="lg"
            iconLeft={Icon.PlusBorder}
            iconType="fill"
            onClick={openAssignModal}
          >
            Assign
          </Common.Button>
        </div>

        {/* Table */}
        <div className="flex flex-col flex-1 min-h-0">
          <CustomScrollbar>
            <Table className="relative w-full border border-line border-separate border-spacing-0">
              <thead>
                <tr>
                  <Th className={tableHeaderClasses}>List name</Th>
                  <Th className={`${tableHeaderClasses} w-[12%]`}>Products</Th>
                  <Th className={`${tableHeaderClasses} w-[20%]`}>
                    Last updated
                  </Th>
                  <Th className={`${tableHeaderClasses} w-[8%] text-center`}>
                    Active
                  </Th>
                  <Th className={`${tableHeaderClasses} w-[6%]`}></Th>
                </tr>
              </thead>
              <tbody>
                {/* Loading */}
                {loading && (
                  <tr>
                    <Td colSpan={5}>
                      <Icon.Loading size={60} className="mx-auto my-36" />
                    </Td>
                  </tr>
                )}

                {/* Empty state */}
                {!loading && assignments.length === 0 && (
                  <tr>
                    <Td
                      colSpan={5}
                      className="text-center py-8 text-neutral-60"
                    >
                      No exclusive lists assigned to this customer.
                    </Td>
                  </tr>
                )}

                {/* Data rows */}
                {!loading &&
                  assignments.map((a) => (
                    <tr key={a.id} className="hover:bg-neutral-5">
                      <Td className={tableCellClasses}>{a.listName}</Td>
                      <Td className={tableCellClasses}>
                        <button
                          className="text-blue-600 underline"
                          onClick={() =>
                            handleShowProducts(a.exclusiveListId, a.listName)
                          }
                        >
                          {a.productCount}
                        </button>
                      </Td>
                      <Td className={tableCellClasses}>
                        {a.updatedDateFormatted}
                      </Td>
                      <Td className={`${tableCellClasses} text-center`}>
                        <div className="flex justify-center">
                          <Common.Checkbox
                            id={`toggle-${a.id}`}
                            ipSize="md"
                            checked={a.isActive}
                            onChange={() => handleToggle(a.id, a.isActive)}
                          />
                        </div>
                      </Td>
                      <Td className={tableCellClasses}>
                        <div className="flex justify-center">
                          <span data-tip="Remove">
                            <Common.Button
                              variant="outline"
                              color="red"
                              size="md"
                              iconType="fill"
                              iconLeft={Icon.Trash}
                              outline
                              className="bg-transparent border-transparent hover:!border-transparent"
                              onClick={() => handleDelete(a.id, a.listName)}
                              isLoading={deleteMuta.isLoading}
                            />
                          </span>
                        </div>
                      </Td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </CustomScrollbar>
        </div>
      </div>

      {/* Assign modal */}
      <ModalContent
        open={showAssignModal}
        onClose={closeAssignModal}
        onOverlayClick={closeAssignModal}
        panelClassWidth="sm:max-w-lg sm:w-full"
        overflowVisible
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-4">Assign exclusive list</h3>
          <Common.CustomCombobox
            key={assignComboboxKey}
            data={unassignedLists as any}
            isLoading={false}
            value={assignSearch}
            placeholder="Search lists to assign..."
            keySearch="name"
            optionsHeader={() => (
              <div className="w-full flex flex-row px-3 py-2 select-none shadow-overline">
                <div className="basis-3/4 font-bold">LIST NAME</div>
                <div className="basis-1/4 font-bold">PRODUCTS</div>
              </div>
            )}
            renderItem={({ item }: any) => (
              <div
                className={clsx(
                  "w-full flex flex-row items-center cursor-pointer px-3 py-4 select-none shadow-overline",
                  "hover:bg-neutral-5",
                  pendingList?.id === item.id ? "bg-blue-10" : ""
                )}
              >
                <div className="basis-3/4">{item.name}</div>
                <div className="basis-1/4 text-neutral-60">
                  {item.productCount}
                </div>
              </div>
            )}
            onSelect={(selected: any) => {
              setPendingList({ id: selected.id, name: selected.name });
              setAssignSearch(selected.name);
            }}
            onChangeInput={(input: string | undefined) => {
              setAssignSearch(input ?? "");
              if (!input) setPendingList(null);
            }}
            optionsClassName="min-w-[400px]"
            noDataDesc="No lists available"
          />
          <div className="pt-2 flex items-center mt-4">
            <Common.Button
              className="ml-auto"
              color="transparent"
              size="lg"
              outline
              onClick={closeAssignModal}
              disabled={assignMuta.isLoading}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="blue"
              size="lg"
              disabled={!pendingList || assignMuta.isLoading}
              onClick={handleAssign}
              isLoading={assignMuta.isLoading}
            >
              Save
            </Common.Button>
          </div>
        </div>
      </ModalContent>

      {/* Products modal */}
      <ModalContent
        open={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        onOverlayClick={() => setShowProductsModal(false)}
        panelClassWidth="sm:max-w-2xl sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-4">
            Products in &quot;{modalListName}&quot;
          </h3>

          {loadingModal ? (
            <Icon.Loading size={60} className="mx-auto my-12" />
          ) : (
            <Table className="w-full border border-line border-separate border-spacing-0">
              <thead>
                <tr>
                  <Th className={`${tableHeaderClasses} w-[20%]`}>SKU</Th>
                  <Th className={tableHeaderClasses}>Name</Th>
                  <Th className={`${tableHeaderClasses} w-[15%]`}>Unit</Th>
                </tr>
              </thead>
              <tbody>
                {modalProducts.length === 0 && (
                  <tr>
                    <Td
                      colSpan={3}
                      className="text-center py-8 text-neutral-60"
                    >
                      No products in this list.
                    </Td>
                  </tr>
                )}
                {modalProducts.map((p, i) => (
                  <tr key={i} className="hover:bg-neutral-5">
                    <Td className={tableCellClasses}>{p.productSKU}</Td>
                    <Td className={tableCellClasses}>{p.productName}</Td>
                    <Td className={tableCellClasses}>{p.productUnit}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className="flex justify-end mt-6">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={() => setShowProductsModal(false)}
            >
              Close
            </Common.Button>
          </div>
        </div>
      </ModalContent>

      {/* Confirm delete modal */}
      <ModalContent
        open={!!confirmDelete}
        onOverlayClick={() => setConfirmDelete(null)}
        panelClassWidth="sm:max-w-sm sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-2">Remove assignment</h3>
          <p className="text-neutral-60 mb-6">
            Are you sure you want to remove the assignment of &quot;
            {confirmDelete?.name}&quot;?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={() => setConfirmDelete(null)}
              disabled={deleteMuta.isLoading}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="red"
              size="lg"
              onClick={handleConfirmDelete}
              isLoading={deleteMuta.isLoading}
            >
              Remove
            </Common.Button>
          </div>
        </div>
      </ModalContent>
    </>
  );
};

export default CustomerExclusiveListPage;
